import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { bundleIcon, MegaphoneLoudFilled, MegaphoneLoudRegular } from '@fluentui/react-icons'
import { Button, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { TimeSuggestionFilterType, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../../pages/Time/BaseTimeSuggestionOverview'
import useQueueStateHandler from '../../../hooks/useQueueStateHandler'
import TimeSuggestionToastContent from '../../../components/Toast/TimeSuggestionToastContent'
import { AxiosResponse } from 'axios'
import useUser from '../../../hooks/useUser'
import { parseFilterValuesToParams } from '../../../pages/BaseOverview/BaseOverview'
import { SortDirection } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'
import { DateTime } from 'luxon'

const Icon = bundleIcon(MegaphoneLoudFilled, MegaphoneLoudRegular)

export default function TimeSuggestionCounter(): ReactElement {
    const axios = useAxios()
    const settings = useSettings()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()

    const handler = useQueueStateHandler<any, TimeSuggestionOverviewDto, any>(props => <TimeSuggestionToastContent {...props} />)

    const [timeSuggestions, setTimeSuggestions] = useState<TimeSuggestionOverviewDto[]>()
    useEffect(() => {
        if (!timeSuggestions || timeSuggestions?.length === 0) {
            return
        }

        const timeSuggestion = timeSuggestions.pop()
        handler.dispatch(timeSuggestion.id, undefined, timeSuggestion)
    }, [timeSuggestions, handler.renderCycle])

    const filterValues = useMemo<TimeSuggestionFilterType>(
        () => ({
            itemUsers: [currentUser?.id],
            statuses: [TimeSuggestionStatus.ToReview],
            from: settings?.timeSuggestionNotificationsDays
                ? DateTime.now().startOf('day').minus({ days: settings.timeSuggestionNotificationsDays }).toUTC().toISODate()
                : undefined,
        }),
        [settings, currentUser],
    )

    if (settings?.timeSuggestionNotificationsDays === 0) {
        return <></>
    }

    return (
        <Tooltip
            content={translate('time.suggestion.showlatest', { count: settings?.timeSuggestionNotificationsDays ?? 0 })}
            relationship={'label'}
            withArrow
        >
            <Button
                icon={<Icon />}
                appearance={'subtle'}
                onClick={() => {
                    axios
                        .get<
                            TimeSuggestionOverviewDto[]
                        >('/time-suggestion', { params: parseFilterValuesToParams(filterValues, { property: ['emailSent', 'id'], direction: SortDirection.Descending }, 0, 5) })
                        .then((response: AxiosResponse<TimeSuggestionOverviewDto[]>) => setTimeSuggestions(response.data))
                }}
            />
        </Tooltip>
    )
}
